import { createFileRoute } from '@tanstack/react-router';
import { Input } from '@osd/ui/input';
import { InputGroup } from '@osd/ui/input-group';
import { Stack } from '@osd/ui/stack';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/input-group — live docs for the `InputGroup` primitive.
 *
 * `InputGroup` fuses an `<Input>` (or any field control) with
 * prefix/suffix text or icon addons into a single bordered surface.
 * The addons collapse borders with the inner field so the group
 * reads as one control (no double-line, no double-radius).
 *
 * The `InputGroupAddon` child component is positioned via a
 * `position` prop ('start' | 'end'). The group adds the right
 * BEM classes to its inner field automatically when an addon is
 * present on each side.
 */
export const Route = createFileRoute('/ui/_layout/input-group')({
  component: InputGroupPage,
});

function InputGroupPage() {
  return (
    <Stack gap={6}>
      <Section title="InputGroup" hint="Borderless addon (start | end) fused with a field control">
        <Stack gap={3}>
          <InputGroup>
            <InputGroup.Addon>@</InputGroup.Addon>
            <Input placeholder="username" />
          </InputGroup>

          <InputGroup>
            <InputGroup.Addon>https://</InputGroup.Addon>
            <Input placeholder="your-handle" />
            <InputGroup.Addon>.bugfix.pro</InputGroup.Addon>
          </InputGroup>

          <InputGroup>
            <Input type="number" defaultValue={42} />
            <InputGroup.Addon>USD</InputGroup.Addon>
          </InputGroup>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Username with @ prefix"
            code={`<InputGroup>
  <InputGroup.Addon>@</InputGroup.Addon>
  <Input placeholder="username" />
</InputGroup>
`}
          />

          <CodeBlock
            language="tsx"
            title="Vanity URL with both addons"
            code={`<InputGroup>
  <InputGroup.Addon>https://</InputGroup.Addon>
  <Input placeholder="your-handle" />
  <InputGroup.Addon>.bugfix.pro</InputGroup.Addon>
</InputGroup>
`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
